import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { getAdminCourseDetailApi, AdminCourseDetail } from "../../features/analytics/api/adminAnalytics.api"
import { LineChart, ChartCard } from "./Charts"
import { ChevronLeftIcon, ArrowTopRightOnSquareIcon, ExclamationTriangleIcon } from "@heroicons/react/16/solid"

const StatCard = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
  <div className="bg-white border border-gray-100 rounded-2xl px-5 py-4">
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{typeof value === "number" ? value.toLocaleString("ru") : value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
)

export const CourseDetailPage = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const [detail, setDetail] = useState<AdminCourseDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    if (!courseId) return
    setLoading(true)
    setError(false)
    getAdminCourseDetailApi(courseId)
      .then((res) => setDetail(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [courseId]) // eslint-disable-line

  if (error) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400 mb-2">Не удалось загрузить курс</p>
        <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
          Повторить
        </button>
      </div>
    )
  }

  if (loading || !detail) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map((i) => <div key={i} className="bg-white border border-gray-100 rounded-2xl h-20 animate-pulse" />)}
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl h-48 animate-pulse" />
      </div>
    )
  }

  const convRate = detail.views > 0
    ? ((detail.purchases / detail.views) * 100).toFixed(1)
    : "0"

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <Link to="/admin/analytics/courses"
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors">
          <ChevronLeftIcon className="w-4 h-4" />
          Курсы
        </Link>
        <span className="text-gray-300">/</span>
        <h2 className="text-base font-semibold text-gray-800 truncate">{detail.title}</h2>
        <Link to={`/admin/analytics/courses/${courseId}/dropout`}
          className="ml-auto flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors">
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
          Анализ отсева
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Просмотров"  value={detail.views}      sub={`${detail.unique_visitors.toLocaleString("ru")} уник.`} />
        <StatCard label="Покупок"     value={detail.purchases} />
        <StatCard label="Конверсия"   value={`${convRate}%`} />
        <StatCard label="Рейтинг"     value={`${detail.avg_rating} ★`} sub={`${detail.reviews_count} отзывов`} />
        <StatCard label="Выручка"     value={`${(detail.revenue / 1000).toFixed(0)}K сом`} sub={`Завершили ${detail.completion_rate}%`} />
      </div>

      {/* Views by day */}
      <ChartCard title="Просмотры по дням">
        {detail.views_by_day.length > 0 ? (
          <LineChart
            data={detail.views_by_day.map((r) => ({ label: r.date, value: r.count }))}
            color="#8b5cf6"
          />
        ) : (
          <div className="flex items-center justify-center h-40">
            <p className="text-sm text-gray-300">Нет данных</p>
          </div>
        )}
      </ChartCard>

      {/* Purchases by day */}
      <ChartCard title="Покупки по дням">
        {detail.purchases_by_day.length > 0 ? (
          <LineChart
            data={detail.purchases_by_day.map((r) => ({ label: r.date, value: r.count }))}
            color="#3b82f6"
          />
        ) : (
          <div className="flex items-center justify-center h-32">
            <p className="text-sm text-gray-300">Нет данных за период</p>
          </div>
        )}
      </ChartCard>
    </div>
  )
}
